import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import {
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Button,
  Space,
  Descriptions,
  Alert,
  Badge,
  Input,
  InputNumber,
  Modal,
  Switch,
  Tooltip,
  message,
  Table,
  Form,
  Spin,
  Select,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  CloudServerOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  EditOutlined,
  DatabaseOutlined,
  EyeOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
};

const { Title, Text } = Typography;

// Interfaces for Settings
interface WarmTargetConfig {
  nodeId: string;
  displayName?: string;
  enabled: boolean;
  targetReady: number;
  minReady: number;
  maxReady: number;
  refillBatchSize: number;
  maxReservationsPerMinute: number;
}

interface WalletFactoryConfig {
  enabled: boolean;
  targetRatePerMinute: number;
  maxConcurrency: number;
  targetInventory: number;
  warmTargets: WarmTargetConfig[];
  walletDeliveryBatchSize?: number;
  skipSignerProbe?: boolean;
}

interface WarmCoordinatorNode {
  nodeId: string;
  displayName: string;
  ready: number;
  reserved: number;
  assigned: number;
  delivered: number;
  retired: number;
  error: number;
  backupPending: number;
  healthy: boolean;
  message?: string | null;
  lastRefreshedAt?: string | null;
}

type WarmCoordinatorState = 'idle' | 'reserving' | 'ready' | 'fallback' | 'error';

interface WarmCoordinatorRuntime {
  state: WarmCoordinatorState;
  lastMessage?: string | null;
  lastUpdated?: string | null;
  nodes: WarmCoordinatorNode[];
}

interface WalletFactoryRuntime {
  activeWorkers: number;
  launched: number;
  succeeded: number;
  failed: number;
  lastError?: string;
  lastRunAt?: string;
  warm?: WarmCoordinatorRuntime;
  // Performance metrics
  averageGenerationTimeMs?: number;
  minGenerationTimeMs?: number;
  maxGenerationTimeMs?: number;
  recentGenerationCount?: number;
}

interface RateLimitedAlert {
  id: string;
  kind: string;
  subject: string;
  severity: 'LOW' | 'HIGH' | 'CRITICAL';
  throttleCount: number;
  firstThrottledAt: string;
  lastThrottledAt: string;
  nextAllowedAt: string;
}

interface WalletFactoryStatus {
  config: WalletFactoryConfig;
  runtime: WalletFactoryRuntime;
  inventory: {
    ready: number;
    assigned: number;
  };
  distribution?: {
    counts: {
      totalWallets?: number;
      ready: number;
      creating?: number;
      verifying?: number;
      assigned: number;
      delivered: number;
      deliveredToMerchant?: number;
      deliveredTotal?: number;
      deliveredToday?: number;
      failed?: number;
      retired?: number;
    };
    latestDeliveredAt: string | null;
    runtime: {
      delivering: boolean;
      lastBatchSize: number;
      lastBatchAt: string | null;
      lastBatchId?: string | null;
      lastError?: string | null;
    };
  };
  nodes?: any[] | null;
  orchestrator?: any | null;
  lastSettingsReceivedAt?: string | null;
  isCronRobotActive?: boolean;
  walletDeliveryBatchSize?: number;
  databaseSize?: {
    databaseName?: string;
    sizeBytes?: number;
    sizeFormatted?: string;
    tableCount?: number;
    largestTables?: Array<{
      table: string;
      sizeBytes: number;
      sizeFormatted: string;
      rowCount: number;
    }>;
  };
  rateLimitedAlerts?: RateLimitedAlert[];
}

type WarmTargetNumericField =
  | 'targetReady'
  | 'minReady'
  | 'maxReady'
  | 'refillBatchSize'
  | 'maxReservationsPerMinute';

const cloneWarmTargets = (targets?: WarmTargetConfig[] | null): WarmTargetConfig[] =>
  (targets ?? []).map((target) => ({ ...target }));


const WARM_CONTROL_DEFS: Array<{
  key: WarmTargetNumericField;
  label: string;
  min: number;
  step?: number;
  tooltip: string;
}> = [
  {
    key: 'targetReady',
    label: 'Target ready',
    min: 0,
    step: 500,
    tooltip:
      'The steady-state inventory each node keeps pre-generated. Nodes automatically top up toward this number after every assignment.',
  },
  {
    key: 'minReady',
    label: 'Trigger floor',
    min: 0,
    step: 500,
    tooltip:
      'When ready inventory dips below this floor the node immediately launches new DKG cycles to refill back to the target.',
  },
  {
    key: 'maxReady',
    label: 'Max ready',
    min: 0,
    step: 500,
    tooltip:
      'Upper guardrail to prevent runaway generation. Nodes stop creating new warm shares once this ceiling is reached.',
  },
  {
    key: 'refillBatchSize',
    label: 'Refill batch size',
    min: 1,
    step: 100,
    tooltip:
      'How many wallets a node generates during each warm refill cycle. Larger batches catch up faster but consume more resources per burst.',
  },
  {
    key: 'maxReservationsPerMinute',
    label: 'Max reservations / min',
    min: 1,
    step: 10,
    tooltip:
      'Rate limit for how many warm shares a node will hand out to the orchestrator per minute to avoid draining the pool too quickly.',
  },
];

const WARM_ENABLED_TOOLTIP =
  'Master switch for the warm pool. When disabled, nodes pause autonomous warm-share generation and only respond to direct DKG requests.';

const TARGET_POOL_TOOLTIP =
  'Global ready-wallet inventory the orchestrator keeps on hand. When the ready count reaches this pool size, new factory jobs pause until demand consumes wallets.';

const WALLETS_PER_MINUTE_TOOLTIP =
  'Target number of wallets to generate per minute. The factory will attempt to maintain this rate while respecting concurrency limits.';

const MAX_CONCURRENCY_TOOLTIP =
  'Maximum number of concurrent wallet generation operations (DKG sessions) that can run simultaneously across all nodes. Higher values increase throughput but consume more resources.';

const WALLET_DELIVERY_BATCH_SIZE_TOOLTIP =
  'Number of wallets to deliver from Aso Rock to AdminMerchantApi in each batch. This controls how many ready wallets are sent per delivery cycle. Higher values deliver more wallets at once but may take longer to process.';

const CRON_ROBOT_ACTIVE_TOOLTIP =
  'Controls whether all automated cron jobs in Aso Rock are active. When disabled, only config sync continues running. All other automated tasks (wallet delivery, runtime updates, sweep child wallets funds, etc.) are paused.';

const FACTORY_ENABLED_TOOLTIP =
  'Master switch for the wallet factory. When enabled, the factory generates new wallets according to the configured rate and concurrency settings. When disabled, wallet generation is paused.';

const SKIP_SIGNER_PROBE_TOOLTIP =
  'Performance mode for wallet generation. When enabled (recommended), wallets are marked ready immediately after DKG (~3s). The signer address is derived mathematically from the public key. When disabled, each wallet goes through an additional MPC signing verification (~100s per wallet).';

// Default warm template - matches the default in the backend database
// This is used as a fallback if the DB doesn't have warmTargets yet
const DEFAULT_WARM_TEMPLATE: WarmTargetConfig = {
  nodeId: 'global',
  displayName: 'All nodes',
  enabled: true,
  targetReady: 10000,
  minReady: 9500,
  maxReady: 12000,
  refillBatchSize: 1000,
  maxReservationsPerMinute: 120,
};

const buildFactoryForm = (config?: WalletFactoryConfig | null): WalletFactoryConfig => {
  if (!config) {
    return {
      enabled: false,
      targetRatePerMinute: 60,
      maxConcurrency: 25, // High throughput default for 2500+ wallets/hour
      targetInventory: 5000,
      warmTargets: [DEFAULT_WARM_TEMPLATE], // Fallback to default template
      walletDeliveryBatchSize: 10,
      skipSignerProbe: true, // Default to fast mode (~3s/wallet)
    };
  }
  return {
    ...config,
    // Use what comes from DB, or fallback to default template if empty
    warmTargets: cloneWarmTargets(
      config.warmTargets && config.warmTargets.length > 0 
        ? config.warmTargets 
        : [DEFAULT_WARM_TEMPLATE]
    ),
    walletDeliveryBatchSize: config.walletDeliveryBatchSize ?? 10,
    skipSignerProbe: config.skipSignerProbe ?? true, // Default to fast mode
  };
};

const MPCService: React.FC = () => {
  // Default nodes that should always be displayed
  // NOTE: These are ONLY fallbacks if database has no data yet
  // The REAL status comes from database (updated by webhook from Aso Rock)
  // Database stores what Aso Rock sends, which matches what Aso Rock UI shows
  const defaultNodes = [
    { id: 'lagos', name: 'Lagos', node: 'lagos', uiEndpoint: null, grpcEndpoint: null, uiPort: 19001, grpcPort: 19101, online: false, state: 'UNKNOWN', rsaKeyConfigured: false, description: 'Waiting for status from Aso Rock...' },
    { id: 'abuja', name: 'Abuja', node: 'abuja', uiEndpoint: null, grpcEndpoint: null, uiPort: 19009, grpcPort: 19109, online: false, state: 'UNKNOWN', rsaKeyConfigured: false, description: 'Waiting for status from Aso Rock...' },
    { id: 'enugu', name: 'Enugu', node: 'enugu', uiEndpoint: null, grpcEndpoint: null, uiPort: 19042, grpcPort: 19142, online: false, state: 'UNKNOWN', rsaKeyConfigured: false, description: 'Waiting for status from Aso Rock...' },
  ];

  // Node Registry state
  const [nodes, setNodes] = useState<any[]>(defaultNodes);
  const [orchestrator, setOrchestrator] = useState<any>(null);

  // Settings state
  const [factoryStatus, setFactoryStatus] = useState<WalletFactoryStatus | null>(null);
  const [factoryLoading, setFactoryLoading] = useState<boolean>(false);
  const [factoryUpdating, setFactoryUpdating] = useState<boolean>(false);
  const [factoryForm, setFactoryForm] = useState<WalletFactoryConfig>(() => buildFactoryForm());
  const [isCronRobotActive, setIsCronRobotActive] = useState<boolean>(false);
  const [warmTargetsSnapshot, setWarmTargetsSnapshot] = useState<WarmTargetConfig[]>([]);
  const [warmTargetsDirty, setWarmTargetsDirty] = useState<boolean>(false);
  const [warmTargetsSaving, setWarmTargetsSaving] = useState<boolean>(false);
  const [workerBeat, setWorkerBeat] = useState(0);
  const [showWarmModal, setShowWarmModal] = useState<boolean>(false);
  const [showFactoryModal, setShowFactoryModal] = useState<boolean>(false);
  
  // Cron Jobs state
  const [cronJobsForm] = Form.useForm();
  const [cronJobsLoading, setCronJobsLoading] = useState(false);
  const [cronJobs, setCronJobs] = useState<any[]>([]);
  const [cronJobModalVisible, setCronJobModalVisible] = useState(false);
  const [editingCronJob, setEditingCronJob] = useState<any>(null);
  const [responseModalVisible, setResponseModalVisible] = useState(false);
  const [selectedCronJobResponse, setSelectedCronJobResponse] = useState<any>(null);

  const warmTargets = factoryForm.warmTargets ?? [];
  // Always use a warm template - either from API data or default, so inputs are always editable
  const warmTemplate = warmTargets.length > 0 ? warmTargets[0] : DEFAULT_WARM_TEMPLATE;
  const warmRuntime = factoryStatus?.runtime.warm;

  const warmBadgeColor =
    warmRuntime?.state === 'error'
      ? 'red'
      : warmRuntime?.state === 'fallback'
      ? 'orange'
      : warmRuntime?.state === 'ready'
      ? 'green'
      : 'default';
  const warmStatusLabel =
    warmRuntime?.state === 'ready'
      ? 'Ready'
      : warmRuntime?.state === 'fallback'
      ? 'Fallback'
      : warmRuntime?.state === 'reserving'
      ? 'Reserving…'
      : warmRuntime?.state === 'error'
      ? 'Error'
      : 'Idle';
  const warmMessage = warmRuntime?.lastMessage ?? 'Warm pool runtime is initialising.';
  const warmAlertType =
    warmRuntime?.state === 'error'
      ? 'error'
      : warmRuntime?.state === 'fallback'
      ? 'warning'
      : undefined;

  // Removed fetchMPCNodes - nodes are now updated directly in fetchFactoryStatus
  // Removed fetchOrchestrator - orchestrator is now updated directly in fetchFactoryStatus

  // Fetch Factory Status
  const fetchFactoryStatus = useCallback(async () => {
    // defaultNodes is a constant, safe to use in callback
    try {
      setFactoryLoading(true);
      const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_MPC_SETTINGS);
      const data: WalletFactoryStatus | null = response.data?.data || null;
      setFactoryStatus(data);
      if (data?.config) {
        const normalized = buildFactoryForm(data.config);
        setFactoryForm(normalized);
        setWarmTargetsSnapshot(cloneWarmTargets(normalized.warmTargets));
        setWarmTargetsDirty(false);
      }
      // Update cron robot active status
      if (data?.isCronRobotActive !== undefined) {
        setIsCronRobotActive(data.isCronRobotActive);
      }
      // Update walletDeliveryBatchSize if available
      if (data?.walletDeliveryBatchSize !== undefined) {
        setFactoryForm(prev => ({
          ...prev,
          walletDeliveryBatchSize: data.walletDeliveryBatchSize,
        }));
      }
      // Update skipSignerProbe if available (from config)
      if (data?.config?.skipSignerProbe !== undefined) {
        setFactoryForm(prev => ({
          ...prev,
          skipSignerProbe: data.config.skipSignerProbe,
        }));
      }
      
      // Update nodes from database (which was updated by webhook from Aso Rock)
      // The database stores the exact status that Aso Rock sent, which matches Aso Rock UI
      console.log('🔍 Raw API data.nodes:', JSON.stringify(data?.nodes, null, 2));
      
      if (data?.nodes && Array.isArray(data.nodes) && data.nodes.length > 0) {
        // Database has node data - use it directly (this is what Aso Rock sent via webhook)
        const apiNodes = data.nodes;
        console.log('📊 Processing API nodes:', apiNodes.length, 'nodes found');
        
        const mergedNodes = defaultNodes.map(defaultNode => {
          // Try to find matching node by id or node field
          const apiNode = apiNodes.find((n: any) => {
            const matchById = n.id === defaultNode.id;
            const matchByNode = n.node === defaultNode.id;
            const matchByName = n.name?.toLowerCase().includes(defaultNode.id.toLowerCase());
            return matchById || matchByNode || matchByName;
          });
          
          if (apiNode) {
            console.log(`✅ Found API node for ${defaultNode.id}:`, { 
              id: apiNode.id, 
              name: apiNode.name, 
              online: apiNode.online, 
              state: apiNode.state 
            });
            
            // Use API data as primary source - only use defaults as fallback for missing fields
            return {
              id: apiNode.id || apiNode.node || defaultNode.id,
              name: apiNode.name || apiNode.displayName || defaultNode.name,
              node: apiNode.node || apiNode.id || defaultNode.node,
              online: apiNode.online !== undefined ? apiNode.online : defaultNode.online,
              state: apiNode.state || defaultNode.state,
              uiEndpoint: apiNode.uiEndpoint || defaultNode.uiEndpoint,
              grpcEndpoint: apiNode.grpcEndpoint || defaultNode.grpcEndpoint,
              uiPort: apiNode.uiPort !== undefined ? apiNode.uiPort : defaultNode.uiPort,
              grpcPort: apiNode.grpcPort !== undefined ? apiNode.grpcPort : defaultNode.grpcPort,
              grpcChannelStatus: apiNode.grpcChannelStatus || (apiNode.online ? 'Ready' : 'Unavailable'),
              fingerprint: apiNode.fingerprint || null,
              rsaKeyConfigured: apiNode.rsaKeyConfigured !== undefined ? apiNode.rsaKeyConfigured : defaultNode.rsaKeyConfigured,
              description: apiNode.description || apiNode.availabilityReason || defaultNode.description,
              grpcHealthy: apiNode.grpcHealthy ?? null,
              httpHealthy: apiNode.httpHealthy ?? null,
            };
          } else {
            console.log(`⚠️ No API node found for ${defaultNode.id}, using default`);
            return defaultNode;
          }
        });
        
        console.log('✅ Final merged nodes:', mergedNodes.map(n => `${n.name}: online=${n.online}, state=${n.state}`));
        setNodes(mergedNodes);
      } else {
        // Database has no node data yet - show defaults until webhook updates database
        console.log('⏳ No node data in database yet - waiting for Aso Rock webhook to update...');
        console.log('📊 data?.nodes:', data?.nodes);
        setNodes(defaultNodes);
      }
      // Always update orchestrator from factory status (even if null, to show default)
      if (data?.orchestrator) {
        setOrchestrator(data.orchestrator);
      } else {
        // Set default orchestrator if not in response
        setOrchestrator({ name: 'Aso Rock', port: 19211, online: false, url: null });
      }
    } catch (error: any) {
      console.error('Failed to fetch wallet factory status', error);
      message.error(error.response?.data?.message || 'Cannot load wallet factory status');
    } finally {
      setFactoryLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // defaultNodes is a constant defined in component, safe to omit from deps

  // Cron Jobs functions
  const fetchCronJobs = async () => {
    try {
      setCronJobsLoading(true);
      const response = await apiClient.get(API_ENDPOINTS.ADMIN_ASO_ROCK_CRON_JOBS);

      if (response.data?.status === 'success' && response.data?.data) {
        setCronJobs(response.data.data);
      } else {
        message.error('Failed to fetch cron jobs');
      }
    } catch (error: any) {
      message.error('Failed to fetch cron jobs');
      console.error('Error fetching cron jobs:', error);
    } finally {
      setCronJobsLoading(false);
    }
  };

  const handleCronJobEdit = (record: any) => {
    setEditingCronJob(record);
    cronJobsForm.setFieldsValue({
      cronPattern: record.cronPattern,
      isActive: record.isActive,
      description: record.description,
    });
    setCronJobModalVisible(true);
  };

  const handleCronJobSubmit = async () => {
    try {
      const values = await cronJobsForm.validateFields();
      if (!editingCronJob) return;

      const response = await apiClient.put(
        API_ENDPOINTS.ADMIN_ASO_ROCK_CRON_JOB_UPDATE.replace('{jobName}', editingCronJob.jobName),
        values
      );

      if (response.data?.status === 'success') {
        message.success('Cron job updated successfully');
        setCronJobModalVisible(false);
        cronJobsForm.resetFields();
        setEditingCronJob(null);
        await fetchCronJobs();
      } else {
        message.error(response.data?.message || 'Failed to update cron job');
      }
    } catch (error: any) {
      message.error('Failed to update cron job');
      console.error('Error updating cron job:', error);
    }
  };

  const handleCronJobCancel = () => {
    setCronJobModalVisible(false);
    cronJobsForm.resetFields();
    setEditingCronJob(null);
  };

  const handleCronJobToggleActive = async (record: any) => {
    try {
      const newActiveStatus = !record.isActive;
      const response = await apiClient.put(
        API_ENDPOINTS.ADMIN_ASO_ROCK_CRON_JOB_UPDATE.replace('{jobName}', record.jobName),
        { isActive: newActiveStatus }
      );

      if (response.data?.status === 'success') {
        message.success(`Cron job ${newActiveStatus ? 'enabled' : 'disabled'} successfully`);
        await fetchCronJobs();
      } else {
        message.error('Failed to update cron job status');
      }
    } catch (error: any) {
      message.error('Failed to update cron job status');
      console.error('Error updating cron job status:', error);
    }
  };

  const getCronDescription = (pattern: string): string => {
    const patterns: { [key: string]: string } = {
      '*/1 * * * *': 'Every minute',
      '*/2 * * * *': 'Every 2 minutes',
      '*/3 * * * *': 'Every 3 minutes',
      '*/4 * * * *': 'Every 4 minutes',
      '*/5 * * * *': 'Every 5 minutes',
      '*/6 * * * *': 'Every 6 minutes',
      '*/7 * * * *': 'Every 7 minutes',
      '*/8 * * * *': 'Every 8 minutes',
      '*/9 * * * *': 'Every 9 minutes',
      '*/10 * * * *': 'Every 10 minutes',
      '*/15 * * * *': 'Every 15 minutes',
      '*/20 * * * *': 'Every 20 minutes',
      '*/30 * * * *': 'Every 30 minutes',
      '0 * * * *': 'Every hour',
      '0 */2 * * *': 'Every 2 hours',
      '0 */6 * * *': 'Every 6 hours',
      '0 */12 * * *': 'Every 12 hours',
      '0 0 * * *': 'Daily at midnight',
      '0 1 * * *': 'Daily at 1:00 AM',
      '0 2 * * *': 'Daily at 2:00 AM',
      '0 3 * * *': 'Daily at 3:00 AM',
      '0 4 * * *': 'Daily at 4:00 AM',
      '0 5 * * *': 'Daily at 5:00 AM',
      '0 6 * * *': 'Daily at 6:00 AM',
      '0 0 * * 0': 'Weekly on Sunday',
      '0 0 1 * *': 'Monthly on the 1st',
      '0 3 28 * *': 'Monthly on the 28th at 3:00 AM',
    };
    return patterns[pattern] || pattern;
  };

  const getJobTypeTag = (jobName: string) => {
    // ADMIN tag cron jobs - only match jobs starting with 'admin-' prefix
    if (jobName.startsWith('admin-') || jobName.startsWith('ADMIN')) {
      return { color: 'magenta', text: 'ADMIN' };
    } else if (jobName.includes('sync-config-from-admin')) {
      return { color: 'purple', text: 'SYNC' };
    } else if (jobName.includes('update-runtime-to-admin')) {
      return { color: 'orange', text: 'RUNTIME' };
    } else if (jobName.includes('sync-resources-from-admin')) {
      return { color: 'geekblue', text: 'RESOURCES' };
    } else if (jobName.includes('deliver-ready-wallets')) {
      return { color: 'cyan', text: 'DELIVERY' };
    } else if (jobName.includes('get-member-assignments')) {
      return { color: 'blue', text: 'ASSIGNMENTS' };
    } else if (jobName.includes('clear-old-notifications')) {
      return { color: 'lime', text: 'CLEANUP' };
    } else if (jobName.includes('main-task')) {
      return { color: 'green', text: 'MAIN' };
    } else if (jobName.includes('sweep-child-wallets-funds') || jobName.includes('sweep')) {
      return { color: 'gold', text: 'SWEEP' };
    } else if (jobName.includes('retry-failed-sweeps')) {
      return { color: 'volcano', text: 'RETRY' };
    }
    return { color: 'default', text: 'OTHER' };
  };

  const handleViewResponse = (record: any) => {
    setSelectedCronJobResponse({
      jobName: record.jobName,
      lastResponseCode: record.lastResponseCode,
      lastResponseBody: record.lastResponseBody,
      lastRun: record.lastRun,
      lastStatus: record.lastStatus,
    });
    setResponseModalVisible(true);
  };

  const getResponseStatusColor = (code: number | null) => {
    if (code === null || code === undefined) return 'default';
    if (code >= 200 && code < 300) return 'success';
    if (code >= 400 && code < 500) return 'warning';
    if (code >= 500) return 'error';
    return 'default';
  };

  const cronJobColumns: ColumnsType<any> = [
    {
      title: 'Job',
      dataIndex: 'jobName',
      key: 'jobName',
      width: 200,
      render: (text: string, record: any) => (
        <Space direction="vertical" size={2} style={{ lineHeight: 1.2 }}>
          <Space size={4}>
            <Tag color={getJobTypeTag(text).color} style={{ margin: 0, fontSize: '11px', padding: '0 6px', lineHeight: '18px' }}>
              {getJobTypeTag(text).text}
            </Tag>
            <Text strong style={{ color: COLORS.textPrimary, fontSize: '13px' }}>
              {text}
            </Text>
          </Space>
          {record.description && (
            <Text style={{ color: COLORS.textSecondary, fontSize: '11px', display: 'block' }}>
              {record.description}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Schedule',
      dataIndex: 'cronPattern',
      key: 'cronPattern',
      width: 180,
      render: (pattern: string) => (
        <Space direction="vertical" size={2} style={{ lineHeight: 1.2 }}>
          <Text code style={{ color: COLORS.accent, fontSize: '11px', padding: '2px 6px' }}>
            {pattern}
          </Text>
          <Text type="secondary" style={{ fontSize: '10px', color: COLORS.textSecondary }}>
            {getCronDescription(pattern)}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      align: 'center' as const,
      render: (isActive: boolean, record: any) => (
        <Switch
          checked={isActive}
          onChange={() => handleCronJobToggleActive(record)}
          checkedChildren="ON"
          unCheckedChildren="OFF"
          size="small"
        />
      ),
    },
    {
      title: 'Last Response',
      key: 'lastResponse',
      width: 120,
      align: 'center' as const,
      render: (_: any, record: any) => {
        const hasResponse = record.lastResponseCode !== null && record.lastResponseCode !== undefined;
        if (!hasResponse) {
          return <Text style={{ color: COLORS.textSecondary, fontSize: '11px' }}>-</Text>;
        }
        return (
          <Space size={4}>
            <Tag 
              color={getResponseStatusColor(record.lastResponseCode)} 
              style={{ margin: 0, fontSize: '11px', padding: '0 6px', lineHeight: '18px' }}
            >
              {record.lastResponseCode}
            </Tag>
            {record.lastResponseBody && (
              <Tooltip title="View Response">
                <Button
                  type="text"
                  icon={<EyeOutlined />}
                  onClick={() => handleViewResponse(record)}
                  size="small"
                  style={{ color: COLORS.accent, padding: '0 4px' }}
                />
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      align: 'center' as const,
      render: (_: any, record: any) => (
        <Tooltip title="Edit cron job">
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleCronJobEdit(record)}
            size="small"
            style={{ backgroundColor: COLORS.accent, color: COLORS.textPrimary, borderColor: COLORS.accent }}
          />
        </Tooltip>
      ),
    },
  ];

  useEffect(() => {
    // Initial fetch - fetch factory status (includes nodes and orchestrator)
    const initialFetch = async () => {
      await fetchFactoryStatus();
      fetchCronJobs();
    };
    initialFetch();
    
    // Auto-refresh factory status every 15 seconds (includes nodes and orchestrator)
    const interval = setInterval(() => {
      fetchFactoryStatus();
    }, 15000);
    const workerInterval = setInterval(() => {
      setWorkerBeat((beat) => beat + 1);
    }, 1000);
    return () => {
      clearInterval(interval);
      clearInterval(workerInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchFactoryStatus]);

  const handleFactoryInputChange = useCallback(
    (field: keyof WalletFactoryConfig, value: number | boolean) => {
      setFactoryForm((prev) => {
        // Validate maxConcurrency to stay within 1-100 range
        if (field === 'maxConcurrency' && typeof value === 'number') {
          const validatedValue = Math.max(1, Math.min(100, value));
          if (validatedValue !== value) {
            message.warning(`Max concurrency must be between 1 and 100. Value adjusted to ${validatedValue}.`);
          }
          return {
            ...prev,
            [field]: validatedValue,
          };
        }
        return {
          ...prev,
          [field]: value,
        };
      });
    },
    []
  );

  const submitFactoryConfig = useCallback(
    async (payload: Partial<WalletFactoryConfig> & { isCronRobotActive?: boolean }) => {
      try {
        setFactoryUpdating(true);
        await apiClient.patch(API_ENDPOINTS.ADMIN_UPDATE_MPC_SETTINGS, payload);
        message.success('Factory config updated');
        await fetchFactoryStatus();
      } catch (error: any) {
        console.error('Failed to update factory config', error);
        message.error(error.response?.data?.message || 'Factory config update failed');
      } finally {
        setFactoryUpdating(false);
      }
    },
    [fetchFactoryStatus]
  );

  const handleFactorySave = useCallback(() => {
    submitFactoryConfig({
      enabled: factoryForm.enabled,
      maxConcurrency: factoryForm.maxConcurrency,
      targetInventory: factoryForm.targetInventory,
      targetRatePerMinute: factoryForm.targetRatePerMinute,
      walletDeliveryBatchSize: factoryForm.walletDeliveryBatchSize ?? 10,
      skipSignerProbe: factoryForm.skipSignerProbe ?? true,
      isCronRobotActive: isCronRobotActive,
    });
  }, [factoryForm, isCronRobotActive, submitFactoryConfig]);

  const handleWarmEnabledToggle = useCallback(
    (enabled: boolean) => {
      setFactoryForm((prev) => ({
        ...prev,
        warmTargets: prev.warmTargets?.map((target, index) =>
          index === 0 ? { ...target, enabled } : target
        ),
      }));
      setWarmTargetsDirty(true);
    },
    []
  );

  const handleWarmNumberChange = useCallback(
    (field: WarmTargetNumericField, value?: number | null) => {
      setFactoryForm((prev) => {
        const targets = prev.warmTargets ?? [];
        if (targets.length === 0) {
          return prev;
        }
        return {
          ...prev,
          warmTargets: targets.map((target, index) =>
            index === 0 && typeof value === 'number'
              ? {
                  ...target,
                  [field]: value,
                }
              : target
          ),
        };
      });
      setWarmTargetsDirty(true);
    },
    []
  );

  const handleWarmTargetsReset = useCallback(() => {
    setFactoryForm((prev) => ({
      ...prev,
      warmTargets: cloneWarmTargets(warmTargetsSnapshot),
    }));
    setWarmTargetsDirty(false);
  }, [warmTargetsSnapshot]);

  const submitWarmTargets = useCallback(
    async (targets: WarmTargetConfig[]) => {
      try {
        setWarmTargetsSaving(true);
        await apiClient.patch(API_ENDPOINTS.ADMIN_UPDATE_MPC_SETTINGS, {
          warmTargets: targets.map((target) => ({
            ...target,
            displayName: undefined,
          })),
        });
        message.success('Warm targets updated');
        await fetchFactoryStatus();
      } catch (error: any) {
        console.error('Failed to update warm targets', error);
        message.error(error.response?.data?.message || 'Warm target update failed');
      } finally {
        setWarmTargetsSaving(false);
      }
    },
    [fetchFactoryStatus]
  );

  const handleWarmTargetsSave = useCallback(() => {
    submitWarmTargets(factoryForm.warmTargets ?? []);
    setWarmTargetsDirty(false);
  }, [factoryForm.warmTargets, submitWarmTargets]);

  const factoryToggleTooltip = factoryForm.enabled
    ? 'Pause the wallet factory to stop launching new workers.'
    : 'Start the wallet factory to resume worker launches.';

  // Dynamic countdown based on DKG time and concurrency
  // Base DKG takes ~40s, with ~5s contention overhead per additional worker
  const activeWorkers = factoryStatus?.runtime.activeWorkers ?? 0;
  const BASE_DKG_SECONDS = 40;
  const CONTENTION_OVERHEAD_PER_WORKER = 5;
  const estimatedCycleSeconds = activeWorkers > 0
    ? BASE_DKG_SECONDS + Math.max(0, activeWorkers - 1) * CONTENTION_OVERHEAD_PER_WORKER
    : 60;
  const workerEtaSeconds = activeWorkers
    ? estimatedCycleSeconds - (workerBeat % estimatedCycleSeconds)
    : 0;
  const workerProgress =
    activeWorkers && workerEtaSeconds
      ? 1 - workerEtaSeconds / estimatedCycleSeconds
      : 0;

  // Calculate elapsed time since last launch (updates every second via workerBeat)
  const lastLaunchElapsed = useMemo(() => {
    // Reference workerBeat to trigger re-computation every second
    void workerBeat;
    if (!factoryStatus?.runtime?.lastRunAt) return null;
    const elapsed = Math.floor((Date.now() - new Date(factoryStatus.runtime.lastRunAt).getTime()) / 1000);
    if (elapsed < 60) return `${elapsed}s ago`;
    if (elapsed < 3600) return `${Math.floor(elapsed / 60)}m ${elapsed % 60}s ago`;
    return dayjs(factoryStatus.runtime.lastRunAt).fromNow();
  }, [factoryStatus?.runtime?.lastRunAt, workerBeat]);

  // Treat node/orchestrator status as "stale" when we haven't received settings
  // from the orchestrator cluster for more than an hour. In that case we keep
  // showing the last known Online/Offline state, but visually downgrade it to
  // an amber/yellow indicator so operators understand it's not real‑time.
  const isStatusStale =
    factoryStatus?.lastSettingsReceivedAt
      ? dayjs().diff(dayjs(factoryStatus.lastSettingsReceivedAt), 'hour') >= 1
      : false;

  return (
    <PageContainer>
      {/* Node Registry Section */}
      <Section>
        <HeaderContainer>
          <Title level={2} style={{ color: COLORS.textPrimary, marginBottom: 0 }}>
            MPC Node Registry
          </Title>
          {factoryStatus?.lastSettingsReceivedAt ? (() => {
            const last = dayjs(factoryStatus.lastSettingsReceivedAt);
            const relative = last.fromNow();
            return (
              <Muted style={{ fontSize: '0.85rem' }}>
                {isStatusStale
                  ? `Status Unknown (last update ${relative})`
                  : `Status Updated ${relative}`}
              </Muted>
            );
          })() : (
            <Muted style={{ fontSize: '0.85rem' }}>
              Status not yet received
            </Muted>
          )}
        </HeaderContainer>

        <Row gutter={[24, 24]}>
          {nodes.map((node: any) => (
            <Col xs={24} sm={12} lg={8} key={node.id}>
              <NodeCardContainer>
                {factoryLoading && (
                  <LoadingOverlay>
                    <Spin size="large" />
                  </LoadingOverlay>
                )}
                <NodeCard
                    title={
                      <Space>
                        <CloudServerOutlined />
                        <span>{node.name}</span>
                      </Space>
                    }
                    extra={
                      <StatusIndicator online={node.online || false} stale={isStatusStale}>
                        {node.online ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                        {node.online ? 'Reachable' : (node.state === 'NOT PROBED' ? 'Pending' : node.state === 'NOT CONFIGURED' ? 'Not Configured' : 'Not Reachable')}
                      </StatusIndicator>
                    }
                  >
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="UI">
                    {node.uiEndpoint ? (
                      <a href={node.uiEndpoint.startsWith('http') ? node.uiEndpoint : `http://${node.uiEndpoint}`} target="_blank" rel="noopener noreferrer">
                        {node.uiEndpoint}
                      </a>
                    ) : (
                      <span>localhost:{node.uiPort || 19009}</span>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="gRPC">
                    {node.grpcEndpoint || `localhost:${node.grpcPort || 19109}`}
                  </Descriptions.Item>
                  <Descriptions.Item label="Health">
                    {(() => {
                      const state = node.state || 'UNKNOWN';
                      if (state === 'NOT PROBED') {
                        return <Tag color="default">Awaiting Probe…</Tag>;
                      }
                      if (state === 'NOT CONFIGURED') {
                        return <Tag color="red" icon={<CloseCircleOutlined />}>Not Configured</Tag>;
                      }
                      return (
                        <Space size={4}>
                          <Tag
                            color={node.grpcHealthy ? 'green' : (state === 'CONNECTING' || state === 'TRANSIENT_FAILURE') ? 'orange' : 'red'}
                            icon={node.grpcHealthy ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                          >
                            {node.grpcHealthy ? 'gRPC ✓' : (state === 'CONNECTING' || state === 'TRANSIENT_FAILURE') ? 'gRPC …' : 'gRPC ✗'}
                          </Tag>
                          <Tag
                            color={node.httpHealthy ? 'green' : 'red'}
                            icon={node.httpHealthy ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                          >
                            {node.httpHealthy ? 'HTTP ✓' : 'HTTP ✗'}
                          </Tag>
                        </Space>
                      );
                    })()}
                  </Descriptions.Item>
                  {!node.online && node.description && (
                    <Descriptions.Item label="Reason">
                      <Tooltip title={node.description}>
                        <Text 
                          type="danger"
                          style={{ 
                            display: 'block',
                            wordBreak: 'break-word',
                            whiteSpace: 'normal',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            lineHeight: '1.4',
                            fontSize: '12px',
                          }}
                        >
                          {node.description.length > 80 
                            ? `${node.description.substring(0, 80)}...` 
                            : node.description}
                        </Text>
                      </Tooltip>
                    </Descriptions.Item>
                  )}
                  {node.fingerprint && (
                    <Descriptions.Item label="Fingerprint">
                      <Text copyable={{ text: node.fingerprint }} style={{ fontFamily: 'monospace', fontSize: '11px' }}>
                        {node.fingerprint.substring(0, 12)}.....{node.fingerprint.slice(-8)}
                      </Text>
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label="RSA Key">
                    {node.rsaKeyConfigured ? (
                      <Tag color="green">Configured</Tag>
                    ) : (
                      <Tag>Not Configured</Tag>
                    )}
                  </Descriptions.Item>
                </Descriptions>
              </NodeCard>
              </NodeCardContainer>
            </Col>
          ))}
          {orchestrator ? (
            <Col xs={24} sm={12} lg={8}>
              <NodeCard
                title={
                  <Space>
                    <CloudServerOutlined />
                    <span>{orchestrator.name} Orchestrator</span>
                  </Space>
                }
                extra={
                  <StatusIndicator online={orchestrator.online || false} stale={isStatusStale}>
                    {orchestrator.online ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                    {orchestrator.online ? 'Online' : 'Offline'}
                  </StatusIndicator>
                }
              >
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Identity">
                    <Text strong style={{ color: COLORS.accent }}>ASOROCK</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Port">
                    {orchestrator.port || 19211}
                  </Descriptions.Item>
                  <Descriptions.Item label="URL">
                    <Text copyable style={{ fontFamily: 'monospace', fontSize: '11px' }}>
                      {orchestrator.url || `localhost:${orchestrator.port || 19211}`}
                    </Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Prod Grafana">
                    <a
                      href="http://grafana-vm.switchwallet.io:3000/d/bekyrqu9pktmoa/switchwallet-logs?orgId=1&from=now-15m&to=now&timezone=browser&var-searchable_pattern=&var-app=enugu"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#f97316', fontWeight: 500 }}
                    >
                      <BarChartOutlined />
                      Open Grafana
                    </a>
                  </Descriptions.Item>
                  {orchestrator.fingerprint && (
                    <Descriptions.Item label="Fingerprint">
                      <Text copyable={{ text: orchestrator.fingerprint }} style={{ fontFamily: 'monospace', fontSize: '11px' }}>
                        {orchestrator.fingerprint.substring(0, 12)}.....{orchestrator.fingerprint.slice(-8)}
                      </Text>
                    </Descriptions.Item>
                  )}
                  {orchestrator.description && (
                    <Descriptions.Item label="Description">
                      <Tooltip title={orchestrator.description}>
                        <Text 
                          style={{ 
                            display: 'block',
                            wordBreak: 'break-word',
                            whiteSpace: 'normal',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            lineHeight: '1.4'
                          }}
                        >
                          {orchestrator.description}
                        </Text>
                      </Tooltip>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </NodeCard>
            </Col>
          ) : null}
        </Row>
      </Section>

      {/* Database Size Section */}
      {factoryStatus?.databaseSize && (
        <Section style={{ marginTop: 24 }}>
          <SectionHeading>
            <SectionTitle>
              <DatabaseOutlined style={{ marginRight: 8 }} />
              Database Storage
            </SectionTitle>
          </SectionHeading>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={8}>
              <NodeCard
                title={
                  <Space>
                    <DatabaseOutlined />
                    <span>AsoRock Database</span>
                  </Space>
                }
              >
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Database">
                    <Text strong style={{ color: COLORS.accent }}>
                      {factoryStatus.databaseSize.databaseName || 'N/A'}
                    </Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Total Size">
                    <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
                      {factoryStatus.databaseSize.sizeFormatted || 'Unknown'}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Table Count">
                    <Text>{factoryStatus.databaseSize.tableCount || 0} tables</Text>
                  </Descriptions.Item>
                </Descriptions>
                {factoryStatus.databaseSize.largestTables && factoryStatus.databaseSize.largestTables.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <Text strong style={{ fontSize: '12px', color: COLORS.textSecondary }}>
                      Largest Tables:
                    </Text>
                    <div style={{ marginTop: 8, maxHeight: '150px', overflowY: 'auto' }}>
                      {factoryStatus.databaseSize.largestTables.slice(0, 5).map((table, idx) => (
                        <div key={idx} style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          padding: '4px 0',
                          borderBottom: `1px solid ${COLORS.border}`,
                          fontSize: '11px'
                        }}>
                          <Text style={{ color: COLORS.textSecondary }}>
                            {table.table.split('.').pop()}
                          </Text>
                          <Space size={8}>
                            <Tag color="default" style={{ fontSize: '10px' }}>
                              {table.rowCount.toLocaleString()} rows
                            </Tag>
                            <Tag color="blue" style={{ fontSize: '10px' }}>
                              {table.sizeFormatted}
                            </Tag>
                          </Space>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </NodeCard>
            </Col>
          </Row>
        </Section>
      )}

      {/* Settings Section */}
      <SettingsPanel>
        <SectionHeading>
          <SectionTitle>Warm Share & Factory Controls</SectionTitle>
          {factoryStatus?.lastSettingsReceivedAt ? (
            <Muted style={{ fontSize: '0.85rem' }}>
              Status Updated {dayjs(factoryStatus?.lastSettingsReceivedAt).fromNow()}
            </Muted>
          ) : (
            <Muted style={{ fontSize: '0.85rem' }}>
              Status not yet received
            </Muted>
          )}
        </SectionHeading>
        <FactoryGrid>
          <ConfigCard>
            <CardTitle>Total Factory Inventory</CardTitle>
            <CardBody>
              <strong style={{ fontSize: '1.5rem' }}>
                {(factoryStatus?.distribution?.counts?.totalWallets ?? 0).toLocaleString()}
              </strong>
              <Muted>wallets generated in Aso Rock</Muted>
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Tag color="green">{(factoryStatus?.distribution?.counts?.ready ?? factoryStatus?.inventory?.ready ?? 0).toLocaleString()} ready</Tag>
                <Tag color="blue">{(factoryStatus?.distribution?.counts?.creating ?? 0).toLocaleString()} creating</Tag>
                <Tag color="purple">{(factoryStatus?.distribution?.counts?.deliveredTotal ?? 0).toLocaleString()} delivered</Tag>
                {(factoryStatus?.distribution?.counts?.failed ?? 0) > 0 && (
                  <Tag color="red">{(factoryStatus?.distribution?.counts?.failed ?? 0).toLocaleString()} failed</Tag>
                )}
              </div>
            </CardBody>
          </ConfigCard>
          <ConfigCard>
            <CardTitle>Current Inventory</CardTitle>
            <CardBody>
              <strong>
                {factoryStatus?.distribution?.counts?.ready ?? factoryStatus?.inventory?.ready ?? 0}{' '}
                ready
              </strong>
              <Muted>
                {factoryStatus?.distribution?.counts?.assigned ?? factoryStatus?.inventory?.assigned ?? 0}{' '}
                assigned
              </Muted>
              <Muted>Delivered: {factoryStatus?.distribution?.counts?.deliveredTotal ?? 0}</Muted>
              <Muted>
                Target pool: {factoryStatus?.config?.targetInventory != null ? factoryStatus?.config?.targetInventory.toLocaleString() : '-'}
                <Tooltip title={TARGET_POOL_TOOLTIP}>
                  <InlineInfoBadge type="button" aria-label="Target pool help">
                    <InfoCircleOutlined />
                  </InlineInfoBadge>
                </Tooltip>
              </Muted>
            </CardBody>
          </ConfigCard>
          <ConfigCard>
            <CardTitle>Live Workers</CardTitle>
            <CardBody>
              <WorkerTelemetry>
                <WorkerPulse $active={(factoryStatus?.runtime.activeWorkers ?? 0) > 0}>
                  <span>{factoryStatus?.runtime.activeWorkers ?? 0}</span>
                  <small>active</small>
                </WorkerPulse>
                <CountdownText>
                  {activeWorkers > 0 && factoryStatus?.runtime?.lastRunAt
                    ? `Next output in ~${workerEtaSeconds}s`
                    : 'Factory idle'}
                </CountdownText>
                {activeWorkers === 0 && (
                  <IdleReasonLabel>
                    {(factoryStatus?.distribution?.counts.ready ?? factoryStatus?.inventory.ready ?? 0) >=
                    (factoryStatus?.config.targetInventory ?? 0)
                      ? 'Target pool reached'
                      : 'Factory stopped'}
                  </IdleReasonLabel>
                )}
                {activeWorkers > 0 && factoryStatus?.runtime?.lastRunAt && (
                  <WorkerProgress>
                    <WorkerProgressFill style={{ width: `${workerProgress * 100}%` }} />
                  </WorkerProgress>
                )}
                <Muted>
                  Last launch:{' '}
                  {lastLaunchElapsed ?? 'No jobs yet'}
                </Muted>
              </WorkerTelemetry>
            </CardBody>
          </ConfigCard>
          <ConfigCard>
            <CardTitle>Runtime</CardTitle>
            <CardBody>
              <span>Active workers: {factoryStatus?.runtime.activeWorkers ?? 0}</span>
              <span>Launched: {factoryStatus?.runtime.launched ?? 0}</span>
              <span>Completed: {factoryStatus?.runtime.succeeded ?? 0}</span>
              <span>Failed: {factoryStatus?.runtime.failed ?? 0}</span>
              {factoryStatus?.runtime?.lastError && (
                <Muted>Last error: {factoryStatus?.runtime?.lastError}</Muted>
              )}
            </CardBody>
          </ConfigCard>
          <ConfigCard>
            <CardTitle>Performance Metrics</CardTitle>
            <CardBody>
              {factoryStatus?.runtime?.averageGenerationTimeMs !== undefined ? (
                <>
                  <span>
                    Avg Generation: <strong>{((factoryStatus.runtime.averageGenerationTimeMs || 0) / 1000).toFixed(2)}s</strong>
                  </span>
                  {factoryStatus.runtime.minGenerationTimeMs !== undefined && (
                    <span>
                      Min: <strong>{((factoryStatus.runtime.minGenerationTimeMs || 0) / 1000).toFixed(2)}s</strong>
                    </span>
                  )}
                  {factoryStatus.runtime.maxGenerationTimeMs !== undefined && (
                    <span>
                      Max: <strong>{((factoryStatus.runtime.maxGenerationTimeMs || 0) / 1000).toFixed(2)}s</strong>
                    </span>
                  )}
                  {factoryStatus.runtime.recentGenerationCount !== undefined && (
                    <Muted>
                      Based on {factoryStatus.runtime.recentGenerationCount} recent wallet(s)
                    </Muted>
                  )}
                </>
              ) : (
                <Muted>No performance data available yet</Muted>
              )}
            </CardBody>
          </ConfigCard>
          <ConfigCard>
            <CardTitle>Delivery</CardTitle>
            <CardBody>
              <span>
                Status:{' '}
                {factoryStatus?.distribution?.runtime.delivering ? 'Delivering' : 'Idle'}
              </span>
              <span>
                Last batch: {factoryStatus?.distribution?.runtime.lastBatchSize ?? 0} wallets
              </span>
              <span>
                Last batch time:{' '}
                {factoryStatus?.distribution?.runtime?.lastBatchAt
                  ? dayjs(factoryStatus?.distribution?.runtime?.lastBatchAt).fromNow()
                  : '—'}
              </span>
              {factoryStatus?.distribution?.runtime?.lastError && (
                <Muted>Last delivery error: {factoryStatus?.distribution?.runtime?.lastError}</Muted>
              )}
            </CardBody>
          </ConfigCard>
          <WarmConfigCard>
            <CardTitle>
              Warm Share Coordinator
              <Tooltip
                title={
                  <div>
                    <div><strong>What it does:</strong> Pre-generates MPC key shares across nodes to speed up wallet creation.</div>
                    <div style={{ marginTop: 8 }}><strong>Why idle:</strong> Goes idle on application restarts. Automatically activates when wallets are generated.</div>
                    <div style={{ marginTop: 8 }}><strong>When it comes back:</strong> Automatically starts when the factory begins generating wallets, or use the manual trigger button in aso rock.</div>
                  </div>
                }
              >
                <InfoCircleOutlined style={{ marginLeft: 8, color: '#1890ff', cursor: 'help' }} />
              </Tooltip>
            </CardTitle>
            <CardBody>
              <WarmStateLine>
                <Tag
                  color={warmBadgeColor === 'default' ? undefined : warmBadgeColor}
                  style={{ marginRight: 8 }}
                >
                  {warmStatusLabel}
                </Tag>
                {warmRuntime?.state === 'reserving' && <DialingIndicator />}
              </WarmStateLine>
              {warmAlertType ? (
                <Alert
                  type={warmAlertType as any}
                  message={warmMessage}
                  showIcon
                  style={{ marginTop: 8, marginBottom: 12 }}
                />
              ) : (
                <WarmMessage>{warmMessage}</WarmMessage>
              )}
              {warmRuntime?.nodes && warmRuntime?.nodes.length > 0 ? (
                <WarmNodeList>
                  {warmRuntime.nodes.map((node) => (
                    <li key={node.nodeId}>
                      <WarmNodeHeader>
                        <strong>{node.displayName}</strong>
                        <Badge
                          status={node.healthy ? 'success' : 'error'}
                          text={node.healthy ? 'Healthy' : 'Offline'}
                        />
                      </WarmNodeHeader>
                      {!node.healthy && node.message && (
                        <Tooltip title={node.message}>
                          <ErrorReason>{node.message}</ErrorReason>
                        </Tooltip>
                      )}
                      <WarmNodeMetrics>
                        <Tag color="blue">Ready {node.ready}</Tag>
                        <Tag color="gold">Reserved {node.reserved}</Tag>
                        <Tag color="purple">Assigned {node.assigned}</Tag>
                        <Tag color="magenta">Delivered {node.delivered}</Tag>
                      </WarmNodeMetrics>
                      {node.message && node.healthy && <Muted>{node.message}</Muted>}
                    </li>
                  ))}
                </WarmNodeList>
              ) : (
                <Muted>Warm pool telemetry pending.</Muted>
              )}
              <WarmTargetsHeader>
                <div>
                  <WarmTargetsTitle>Per-node warm pool controls</WarmTargetsTitle>
                  <WarmTargetsHint>
                    Tune each city's warm share thresholds, refill batch size, and reservation
                    guardrails.
                  </WarmTargetsHint>
                </div>
                <WarmTargetActions>
                  <GoldenButton type="primary" size="small" onClick={() => setShowWarmModal(true)}>
                    Configure Warm Pool
                  </GoldenButton>
                </WarmTargetActions>
              </WarmTargetsHeader>
              <WarmGlobalControls>
                <WarmDataDisplay>
                  <WarmDataRow>
                    <WarmDataLabel>Warm pool enabled:</WarmDataLabel>
                    <WarmDataValue>
                      {warmTemplate ? (
                        <Tag color={warmTemplate.enabled ? 'green' : 'red'}>
                          {warmTemplate.enabled ? 'Enabled' : 'Disabled'}
                        </Tag>
                      ) : (
                        '-'
                      )}
                    </WarmDataValue>
                  </WarmDataRow>
                  {WARM_CONTROL_DEFS.map((control) => (
                    <WarmDataRow key={control.key}>
                      <WarmDataLabel>
                        {control.label}:
                        <Tooltip title={control.tooltip}>
                          <InfoBadge type="button" aria-label={`${control.label} help`}>
                            <InfoCircleOutlined />
                          </InfoBadge>
                        </Tooltip>
                      </WarmDataLabel>
                      <WarmDataValue>
                        {warmTemplate ? warmTemplate[control.key]?.toLocaleString() : '-'}
                      </WarmDataValue>
                    </WarmDataRow>
                  ))}
                </WarmDataDisplay>
                {warmTargets.length === 0 && (
                  <Muted>Warm pool configuration will activate once nodes report in.</Muted>
                )}
              </WarmGlobalControls>
            </CardBody>
          </WarmConfigCard>
        </FactoryGrid>
        <FactoryControlsCard>
          <CardTitle>Factory Controls</CardTitle>
          <CardBody>
            <FactoryDataDisplay>
              <FactoryDataRow>
                <FactoryDataLabel>
                  Cron Robot Active:
                  <Tooltip title={CRON_ROBOT_ACTIVE_TOOLTIP}>
                    <InlineInfoBadge type="button" aria-label="Cron robot active help">
                      <InfoCircleOutlined />
                    </InlineInfoBadge>
                  </Tooltip>
                </FactoryDataLabel>
                <FactoryDataValue>
                  <Tag color={isCronRobotActive ? 'green' : 'red'}>
                    {isCronRobotActive ? 'Active' : 'Inactive'}
                  </Tag>
                </FactoryDataValue>
              </FactoryDataRow>
              <FactoryDataRow>
                <FactoryDataLabel>
                  Factory status:
                  <Tooltip title={FACTORY_ENABLED_TOOLTIP}>
                    <InlineInfoBadge type="button" aria-label="Factory status help">
                      <InfoCircleOutlined />
                    </InlineInfoBadge>
                  </Tooltip>
                </FactoryDataLabel>
                <FactoryDataValue>
                  <Tag color={factoryForm.enabled ? 'green' : 'red'}>
                    {factoryForm.enabled ? 'Running' : 'Paused'}
                  </Tag>
                </FactoryDataValue>
              </FactoryDataRow>
              <FactoryDataRow>
                <FactoryDataLabel>
                  Wallets per minute:
                  <Tooltip title={WALLETS_PER_MINUTE_TOOLTIP}>
                    <InlineInfoBadge type="button" aria-label="Wallets per minute help">
                      <InfoCircleOutlined />
                    </InlineInfoBadge>
                  </Tooltip>
                </FactoryDataLabel>
                <FactoryDataValue>{factoryForm.targetRatePerMinute}</FactoryDataValue>
              </FactoryDataRow>
              <FactoryDataRow>
                <FactoryDataLabel>
                  Max concurrency:
                  <Tooltip title={MAX_CONCURRENCY_TOOLTIP}>
                    <InlineInfoBadge type="button" aria-label="Max concurrency help">
                      <InfoCircleOutlined />
                    </InlineInfoBadge>
                  </Tooltip>
                </FactoryDataLabel>
                <FactoryDataValue>{factoryForm.maxConcurrency}</FactoryDataValue>
              </FactoryDataRow>
              <FactoryDataRow>
                <FactoryDataLabel>
                  Target inventory:
                  <Tooltip title={TARGET_POOL_TOOLTIP}>
                    <InlineInfoBadge type="button" aria-label="Target inventory help">
                      <InfoCircleOutlined />
                    </InlineInfoBadge>
                  </Tooltip>
                </FactoryDataLabel>
                <FactoryDataValue>
                  {factoryForm.targetInventory != null ? factoryForm.targetInventory.toLocaleString() : '-'}
                </FactoryDataValue>
              </FactoryDataRow>
              <FactoryDataRow>
                <FactoryDataLabel>
                  Wallet delivery batch size:
                  <Tooltip title={WALLET_DELIVERY_BATCH_SIZE_TOOLTIP}>
                    <InlineInfoBadge type="button" aria-label="Wallet delivery batch size help">
                      <InfoCircleOutlined />
                    </InlineInfoBadge>
                  </Tooltip>
                </FactoryDataLabel>
                <FactoryDataValue>
                  {factoryForm.walletDeliveryBatchSize ?? 10}
                </FactoryDataValue>
              </FactoryDataRow>
              <FactoryDataRow>
                <FactoryDataLabel>
                  Generation mode:
                  <Tooltip title={SKIP_SIGNER_PROBE_TOOLTIP}>
                    <InlineInfoBadge type="button" aria-label="Generation mode help">
                      <InfoCircleOutlined />
                    </InlineInfoBadge>
                  </Tooltip>
                </FactoryDataLabel>
                <FactoryDataValue>
                  <Tag color={factoryForm.skipSignerProbe !== false ? 'green' : 'orange'}>
                    {factoryForm.skipSignerProbe !== false ? '⚡ Fast (~3s)' : '🐢 Verified (~100s)'}
                  </Tag>
                </FactoryDataValue>
              </FactoryDataRow>
            </FactoryDataDisplay>
            <ControlButtonRow>
              <GoldenButton type="primary" onClick={() => setShowFactoryModal(true)}>
                Configure Factory
              </GoldenButton>
            </ControlButtonRow>
          </CardBody>
        </FactoryControlsCard>
      </SettingsPanel>

      {/* Aso Rock Cron Jobs Section */}
      <Section>
        <HeaderContainer>
          <Title level={2} style={{ color: COLORS.textPrimary, marginBottom: 0 }}>
            Aso Rock Cron Jobs
          </Title>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchCronJobs}
            loading={cronJobsLoading}
          >
            Refresh
          </Button>
        </HeaderContainer>
        <NodeCard>
          <Table
            columns={cronJobColumns as any}
            dataSource={cronJobs}
            rowKey="id"
            loading={cronJobsLoading}
            pagination={false}
            size="small"
            style={{ background: COLORS.bgSecondary }}
          />
        </NodeCard>
      </Section>

      {/* Rate-Limited Alerts Section */}
      {factoryStatus?.rateLimitedAlerts && factoryStatus.rateLimitedAlerts.length > 0 && (
        <Section>
          <HeaderContainer>
            <Title level={2} style={{ color: COLORS.textPrimary, marginBottom: 0 }}>
              Rate-Limited Alerts
            </Title>
            <Tooltip title="Notifications are throttled to max 1 per type every 6 hours (max 4 per day). These alerts were received but rate-limited.">
              <InfoCircleOutlined style={{ color: COLORS.accent, fontSize: 18 }} />
            </Tooltip>
          </HeaderContainer>
          <RateLimitedAlertsCard>
            <Text type="secondary" style={{ marginBottom: 12, display: 'block' }}>
              The following notifications were rate-limited. Each alert type is limited to 1 notification every 6 hours.
            </Text>
            <RateLimitedAlertsList>
              {factoryStatus.rateLimitedAlerts.map((alert) => (
                <RateLimitedAlertItem key={alert.id}>
                  <RateLimitedAlertHeader>
                    <Tag color={
                      alert.severity === 'CRITICAL' ? 'red' :
                      alert.severity === 'HIGH' ? 'orange' : 'blue'
                    }>
                      {alert.severity}
                    </Tag>
                    <Text strong style={{ color: COLORS.textPrimary, flex: 1 }}>
                      {alert.subject}
                    </Text>
                    <Badge 
                      count={`${alert.throttleCount}x throttled`}
                      style={{ backgroundColor: COLORS.accent }}
                    />
                  </RateLimitedAlertHeader>
                  <RateLimitedAlertMeta>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Last throttled: {dayjs(alert.lastThrottledAt).fromNow()}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Next allowed: {dayjs(alert.nextAllowedAt).fromNow()}
                    </Text>
                  </RateLimitedAlertMeta>
                </RateLimitedAlertItem>
              ))}
            </RateLimitedAlertsList>
          </RateLimitedAlertsCard>
        </Section>
      )}

      {/* Warm Pool Configuration Modal */}
      <Modal
        title="Configure Warm Pool"
        open={showWarmModal}
        onCancel={() => setShowWarmModal(false)}
        width={600}
        footer={[
          <Button
            key="reset"
            onClick={handleWarmTargetsReset}
            disabled={!warmTargetsDirty || warmTargetsSaving}
          >
            Reset
          </Button>,
          <Button key="cancel" onClick={() => setShowWarmModal(false)}>
            Cancel
          </Button>,
          <GoldenButton
            key="save"
            type="primary"
            onClick={() => {
              handleWarmTargetsSave();
              setShowWarmModal(false);
            }}
            loading={warmTargetsSaving}
            disabled={!warmTargetsDirty}
          >
            Save Changes
          </GoldenButton>,
        ]}
        bodyStyle={{ background: '#161b22', color: '#f0f6fc' }}
      >
        <ModalContent>
          <ModalSection>
            <ModalSectionTitle>Warm Pool Settings</ModalSectionTitle>
            <ModalField>
              <ModalFieldLabel>
                Warm pool enabled
                <Tooltip title={WARM_ENABLED_TOOLTIP}>
                  <InfoBadge type="button" aria-label="Warm pool enablement help">
                    <InfoCircleOutlined />
                  </InfoBadge>
                </Tooltip>
              </ModalFieldLabel>
              <Switch checked={warmTemplate.enabled ?? false} onChange={handleWarmEnabledToggle} />
              <ModalFieldHint>
                Keep this on so Lagos, Abuja, and Enugu continuously pre-build shares up to the
                target.
              </ModalFieldHint>
            </ModalField>
          </ModalSection>

          <ModalSection>
            <ModalSectionTitle>Pool Thresholds</ModalSectionTitle>
            <ModalControlsGrid>
              {WARM_CONTROL_DEFS.map((control) => (
                <ModalControlField key={control.key}>
                  <ModalControlLabel>
                    {control.label}
                    <Tooltip title={control.tooltip}>
                      <InfoBadge type="button" aria-label={`${control.label} help`}>
                        <InfoCircleOutlined />
                      </InfoBadge>
                    </Tooltip>
                  </ModalControlLabel>
                  <InputNumber
                    min={control.min}
                    step={control.step}
                    value={warmTemplate[control.key] ?? 0}
                    onChange={(value) => handleWarmNumberChange(control.key, value)}
                    style={{ width: '100%' }}
                  />
                </ModalControlField>
              ))}
            </ModalControlsGrid>
          </ModalSection>
        </ModalContent>
      </Modal>

      {/* Factory Configuration Modal */}
      <Modal
        title="Configure Factory"
        open={showFactoryModal}
        onCancel={() => setShowFactoryModal(false)}
        width={600}
        footer={[
          <Button key="cancel" onClick={() => setShowFactoryModal(false)}>
            Cancel
          </Button>,
          <GoldenButton
            key="save"
            type="primary"
            onClick={() => {
              handleFactorySave();
              setShowFactoryModal(false);
            }}
            loading={factoryUpdating}
          >
            Save Settings
          </GoldenButton>,
        ]}
        bodyStyle={{ background: '#161b22', color: '#f0f6fc' }}
      >
        <ModalContent>
          <ModalSection>
            <ModalSectionTitle>Factory Settings</ModalSectionTitle>
            <ModalField>
              <ModalFieldLabel>
                Cron Robot Active
                <Tooltip title="Controls whether all automated cron jobs in Aso Rock are active. When disabled, only config sync continues running. All other automated tasks (wallet delivery, runtime updates, sweep child wallets funds, etc.) are paused.">
                  <InfoBadge type="button" aria-label="Cron robot active help">
                    <InfoCircleOutlined />
                  </InfoBadge>
                </Tooltip>
              </ModalFieldLabel>
              <Switch
                checked={isCronRobotActive}
                onChange={(checked) => setIsCronRobotActive(checked)}
              />
              <ModalFieldHint>
                {isCronRobotActive
                  ? 'All automated cron jobs in Aso Rock are active and running.'
                  : 'All automated cron jobs in Aso Rock are disabled. Only config sync continues running.'}
              </ModalFieldHint>
            </ModalField>
            <ModalField>
              <ModalFieldLabel>
                Factory enabled
                <Tooltip title={factoryToggleTooltip}>
                  <InfoBadge type="button" aria-label="Factory enablement help">
                    <InfoCircleOutlined />
                  </InfoBadge>
                </Tooltip>
              </ModalFieldLabel>
              <Switch
                checked={factoryForm.enabled}
                onChange={(checked) => handleFactoryInputChange('enabled', checked)}
              />
              <ModalFieldHint>
                {factoryForm.enabled
                  ? 'Factory is running and will generate wallets according to the settings below.'
                  : 'Factory is paused. Enable it to start generating wallets.'}
              </ModalFieldHint>
            </ModalField>
            <ModalField>
              <ModalFieldLabel>
                Wallets per minute
                <Tooltip title={WALLETS_PER_MINUTE_TOOLTIP}>
                  <InfoBadge type="button" aria-label="Wallets per minute help">
                    <InfoCircleOutlined />
                  </InfoBadge>
                </Tooltip>
              </ModalFieldLabel>
              <Input
                type="number"
                min={0}
                value={factoryForm.targetRatePerMinute}
                onChange={(event) =>
                  handleFactoryInputChange('targetRatePerMinute', Number(event.target.value))
                }
              />
            </ModalField>
            <ModalField>
              <ModalFieldLabel>
                Max concurrency (1-100)
                <Tooltip title={MAX_CONCURRENCY_TOOLTIP}>
                  <InfoBadge type="button" aria-label="Max concurrency help">
                    <InfoCircleOutlined />
                  </InfoBadge>
                </Tooltip>
              </ModalFieldLabel>
              <InputNumber
                min={1}
                max={100}
                value={factoryForm.maxConcurrency}
                onChange={(value) =>
                  handleFactoryInputChange('maxConcurrency', value || 1)
                }
                style={{ width: '100%' }}
              />
            </ModalField>
            <ModalField>
              <ModalFieldLabel>
                Target inventory
                <Tooltip title={TARGET_POOL_TOOLTIP}>
                  <InfoBadge type="button" aria-label="Target inventory help">
                    <InfoCircleOutlined />
                  </InfoBadge>
                </Tooltip>
              </ModalFieldLabel>
              <Input
                type="number"
                min={10}
                value={factoryForm.targetInventory}
                onChange={(event) =>
                  handleFactoryInputChange('targetInventory', Number(event.target.value))
                }
              />
            </ModalField>
            <ModalField>
              <ModalFieldLabel>
                Wallet delivery batch size
                <Tooltip title={WALLET_DELIVERY_BATCH_SIZE_TOOLTIP}>
                  <InfoBadge type="button" aria-label="Wallet delivery batch size help">
                    <InfoCircleOutlined />
                  </InfoBadge>
                </Tooltip>
              </ModalFieldLabel>
              <InputNumber
                min={1}
                max={100}
                value={factoryForm.walletDeliveryBatchSize ?? 10}
                onChange={(value) =>
                  handleFactoryInputChange('walletDeliveryBatchSize', value || 10)
                }
                style={{ width: '100%' }}
              />
            </ModalField>
            <ModalField>
              <ModalFieldLabel>
                Fast generation mode (skip signer verification)
                <Tooltip title={SKIP_SIGNER_PROBE_TOOLTIP}>
                  <InfoBadge type="button" aria-label="Fast generation mode help">
                    <InfoCircleOutlined />
                  </InfoBadge>
                </Tooltip>
              </ModalFieldLabel>
              <Switch
                checked={factoryForm.skipSignerProbe !== false}
                onChange={(checked) => handleFactoryInputChange('skipSignerProbe', checked)}
              />
              <ModalFieldHint>
                {factoryForm.skipSignerProbe !== false
                  ? '⚡ Fast mode: Wallets ready in ~3 seconds. Signer address derived from public key (cryptographically guaranteed).'
                  : '🐢 Verified mode: Each wallet undergoes MPC signing verification (~100s). Only use if you need to verify each wallet before use.'}
              </ModalFieldHint>
            </ModalField>
          </ModalSection>
        </ModalContent>
      </Modal>

      {/* Cron Job Edit Modal */}
      <Modal
        title={`Edit Cron Job: ${editingCronJob?.jobName}`}
        open={cronJobModalVisible}
        onOk={handleCronJobSubmit}
        onCancel={handleCronJobCancel}
        okText="Update"
        cancelText="Cancel"
        width={600}
        bodyStyle={{ background: '#161b22', color: '#f0f6fc' }}
      >
        <Form
          form={cronJobsForm}
          layout="vertical"
          style={{ marginTop: 20 }}
        >
          <Form.Item
            label="Schedule"
            name="cronPattern"
            rules={[
              { required: true, message: 'Please select a schedule' },
            ]}
            tooltip="Select how often this cron job should run"
          >
            <Select placeholder="Select schedule">
              <Select.Option value="*/1 * * * *">Every 1 minute</Select.Option>
              <Select.Option value="*/2 * * * *">Every 2 minutes</Select.Option>
              <Select.Option value="*/5 * * * *">Every 5 minutes</Select.Option>
              <Select.Option value="*/10 * * * *">Every 10 minutes</Select.Option>
              <Select.Option value="*/15 * * * *">Every 15 minutes</Select.Option>
              <Select.Option value="*/30 * * * *">Every 30 minutes</Select.Option>
              <Select.Option value="0 * * * *">Every hour</Select.Option>
              <Select.Option value="0 */2 * * *">Every 2 hours</Select.Option>
              <Select.Option value="0 */6 * * *">Every 6 hours</Select.Option>
              <Select.Option value="0 */12 * * *">Every 12 hours</Select.Option>
              <Select.Option value="0 0 * * *">Daily at midnight</Select.Option>
              <Select.Option value="0 0 * * 0">Weekly on Sunday</Select.Option>
              <Select.Option value="0 0 1 * *">Monthly on the 1st</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Active"
            name="isActive"
            valuePropName="checked"
          >
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
          >
            <Input.TextArea 
              rows={4} 
              readOnly 
              disabled
              style={{ 
                backgroundColor: COLORS.bgSecondary,
                color: COLORS.textSecondary,
                cursor: 'not-allowed'
              }}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Cron Job Response Modal */}
      <Modal
        title={
          <Space>
            <EyeOutlined />
            <span>Last Response: {selectedCronJobResponse?.jobName}</span>
            {selectedCronJobResponse?.lastResponseCode && (
              <Tag color={getResponseStatusColor(selectedCronJobResponse.lastResponseCode)}>
                {selectedCronJobResponse.lastResponseCode}
              </Tag>
            )}
          </Space>
        }
        open={responseModalVisible}
        onCancel={() => {
          setResponseModalVisible(false);
          setSelectedCronJobResponse(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setResponseModalVisible(false);
            setSelectedCronJobResponse(null);
          }}>
            Close
          </Button>
        ]}
        width={800}
        bodyStyle={{ background: '#161b22', color: '#f0f6fc', maxHeight: '70vh', overflow: 'auto' }}
      >
        {selectedCronJobResponse && (
          <Space direction="vertical" style={{ width: '100%' }} size={16}>
            <Descriptions
              bordered
              size="small"
              column={2}
              labelStyle={{ background: COLORS.bgSecondary, color: COLORS.textSecondary }}
              contentStyle={{ background: COLORS.bgPrimary, color: COLORS.textPrimary }}
            >
              <Descriptions.Item label="Job Name">
                {selectedCronJobResponse.jobName}
              </Descriptions.Item>
              <Descriptions.Item label="Status Code">
                <Tag color={getResponseStatusColor(selectedCronJobResponse.lastResponseCode)}>
                  {selectedCronJobResponse.lastResponseCode || 'N/A'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Last Run">
                {selectedCronJobResponse.lastRun 
                  ? dayjs(selectedCronJobResponse.lastRun).format('YYYY-MM-DD HH:mm:ss')
                  : 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Last Status">
                <Tag color={selectedCronJobResponse.lastStatus === 'success' ? 'success' : selectedCronJobResponse.lastStatus === 'error' ? 'error' : 'processing'}>
                  {selectedCronJobResponse.lastStatus || 'unknown'}
                </Tag>
              </Descriptions.Item>
            </Descriptions>

            <div>
              <Text strong style={{ color: COLORS.textPrimary, display: 'block', marginBottom: 8 }}>
                Response Body:
              </Text>
              <pre style={{
                background: COLORS.bgPrimary,
                border: `1px solid ${COLORS.border}`,
                borderRadius: 6,
                padding: 16,
                overflow: 'auto',
                maxHeight: '400px',
                fontSize: '12px',
                color: COLORS.textPrimary,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}>
                {selectedCronJobResponse.lastResponseBody 
                  ? (() => {
                      try {
                        // Try to parse and pretty-print JSON
                        const parsed = JSON.parse(selectedCronJobResponse.lastResponseBody);
                        return JSON.stringify(parsed, null, 2);
                      } catch {
                        // If not valid JSON, return as-is
                        return selectedCronJobResponse.lastResponseBody;
                      }
                    })()
                  : 'No response body available'}
              </pre>
            </div>
          </Space>
        )}
      </Modal>
    </PageContainer>
  );
};

export default MPCService;

// Styled Components
const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const HeaderContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h2 {
    margin-bottom: 0 !important;
    font-size: 24px !important;
    font-weight: 600 !important;
  }
`;

const NodeCardContainer = styled.div`
  position: relative;
  margin-bottom: 24px;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 8px;
`;

const NodeCard = styled(Card)`
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  overflow: hidden !important;
  word-wrap: break-word !important;
  
  .ant-card-head {
    border-bottom: 1px solid ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
    background: ${COLORS.bgSecondary} !important;
    
    .ant-card-head-title {
      color: ${COLORS.textPrimary} !important;
    }
  }
  
  .ant-card-body {
    color: ${COLORS.textPrimary} !important;
    background: ${COLORS.bgSecondary} !important;
  }
  
  .ant-descriptions {
    color: ${COLORS.textPrimary} !important;
    
    .ant-descriptions-item-label {
      color: ${COLORS.textSecondary} !important;
      font-weight: 500;
    }
    
    .ant-descriptions-item-content {
      color: ${COLORS.textPrimary} !important;
      max-width: 100% !important;
      word-break: break-word !important;
      overflow-wrap: break-word !important;
      overflow: hidden !important;
    }
  }
  
  .ant-tag {
    color: ${COLORS.textPrimary} !important;
    background: ${COLORS.bgPrimary} !important;
    border-color: ${COLORS.border} !important;
    max-width: 100% !important;
    word-break: break-word !important;
    white-space: normal !important;
    overflow: hidden !important;
    
    &.ant-tag-green {
      background: rgba(16, 185, 129, 0.15) !important;
      border-color: rgba(16, 185, 129, 0.3) !important;
      color: #10B981 !important;
    }
    
    &.ant-tag-red {
      background: rgba(239, 68, 68, 0.15) !important;
      border-color: rgba(239, 68, 68, 0.3) !important;
      color: #EF4444 !important;
    }
    
    &.ant-tag-volcano {
      background: rgba(255, 77, 79, 0.15) !important;
      border-color: rgba(255, 77, 79, 0.3) !important;
      color: #FF4D4F !important;
      max-width: 100% !important;
      word-break: break-word !important;
      white-space: normal !important;
      overflow: hidden !important;
    }
    
    &.ant-tag-default {
      background: ${COLORS.bgPrimary} !important;
      border-color: ${COLORS.border} !important;
      color: ${COLORS.textSecondary} !important;
    }
  }
  
  .ant-typography {
    color: ${COLORS.textPrimary} !important;
  }
  
  span {
    color: ${COLORS.textPrimary} !important;
  }
  
  code {
    background: ${COLORS.bgPrimary} !important;
    color: ${COLORS.textPrimary} !important;
    border: 1px solid ${COLORS.border} !important;
  }
`;

const StatusIndicator = styled.div<{ online: boolean; stale?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px;
  border-radius: 6px;
  background: ${({ online, stale }) =>
    stale
      ? 'rgba(250, 204, 21, 0.12)' // amber for stale status (unknown freshness)
      : online
      ? 'rgba(16, 185, 129, 0.1)' // green for online
      : 'rgba(239, 68, 68, 0.1)' // red for offline
  };
  color: ${({ online, stale }) =>
    stale
      ? '#FBBF24'
      : online
      ? '#10B981'
      : '#EF4444'
  };
  font-size: 12px;
  font-weight: 600;
`;

// Settings Panel Styled Components
const SettingsPanel = styled.div`
  background: #161b22;
  border: 1px solid #30363d;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 8px 24px rgba(1, 4, 9, 0.4);
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SectionHeading = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SectionTitle = styled.h2`
  margin: 0;
  color: #f0f6fc;
  font-size: 1.2rem;
`;

const FactoryGrid = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
`;

const ConfigCard = styled.div`
  background: #0d1117;
  border: 1px solid #21262d;
  border-radius: 12px;
  padding: 16px;
`;

const WarmConfigCard = styled(ConfigCard)`
  grid-column: 1 / -1;
`;

const CardTitle = styled.h3`
  margin: 0 0 8px 0;
  color: #f0f6fc;
`;

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  color: #c9d1d9;
`;

const Muted = styled.span`
  color: #8b949e;
  font-size: 0.9rem;
  word-break: break-word;
  overflow-wrap: break-word;
`;

const ErrorReason = styled.div`
  color: #f85149;
  font-size: 0.78rem;
  line-height: 1.4;
  word-break: break-word;
  overflow-wrap: break-word;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  cursor: help;
`;

const IdleReasonLabel = styled.div`
  color: #dc3545;
  font-size: 0.85rem;
  font-weight: 600;
  margin-top: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const WarmStateLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #f0f6fc;
`;

const WarmMessage = styled.p`
  margin: 8px 0;
  color: #8b949e;
`;

const WarmNodeList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;

  li {
    border: 1px solid #30363d;
    border-radius: 8px;
    padding: 10px 14px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-height: 120px;
  }
  
  /* Fix Badge text color - ensure it's visible on dark background */
  .ant-badge-status-text {
    color: #f0f6fc !important;
  }
  
  .ant-badge-status-success .ant-badge-status-text {
    color: #10B981 !important;
  }
  
  .ant-badge-status-error .ant-badge-status-text {
    color: #EF4444 !important;
  }
`;

const WarmNodeHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;
  max-width: 100%;

  strong {
    color: #f0f6fc !important;
    flex-shrink: 0;
  }
  
  .ant-tag {
    flex: 1 1 auto;
    min-width: 0;
    max-width: 100%;
    color: inherit !important;
  }
  
  .ant-badge {
    color: inherit !important;
    
    .ant-badge-status-text {
      color: #f0f6fc !important;
    }
  }
  
  .ant-badge-status-success .ant-badge-status-text {
    color: #10B981 !important;
  }
  
  .ant-badge-status-error .ant-badge-status-text {
    color: #EF4444 !important;
  }
`;

const WarmNodeMetrics = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  
  .ant-tag {
    color: #f0f6fc !important;
    border-color: rgba(240, 246, 252, 0.2) !important;
  }
  
  .ant-tag-blue {
    background: rgba(88, 166, 255, 0.2) !important;
    color: #58a6ff !important;
    border-color: rgba(88, 166, 255, 0.4) !important;
  }
  
  .ant-tag-gold {
    background: rgba(251, 188, 5, 0.2) !important;
    color: #fbbc05 !important;
    border-color: rgba(251, 188, 5, 0.4) !important;
  }
  
  .ant-tag-purple {
    background: rgba(163, 113, 247, 0.2) !important;
    color: #a371f7 !important;
    border-color: rgba(163, 113, 247, 0.4) !important;
  }
  
  .ant-tag-magenta {
    background: rgba(255, 80, 203, 0.2) !important;
    color: #ff50cb !important;
    border-color: rgba(255, 80, 203, 0.4) !important;
  }
`;

const WarmTargetsHeader = styled.div`
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #30363d;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const WarmTargetsTitle = styled.div`
  font-weight: 600;
  color: #f0f6fc;
`;

const WarmTargetsHint = styled.p`
  margin: 4px 0 0;
  color: #8b949e;
  font-size: 0.85rem;
`;

const WarmTargetActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const WarmGlobalControls = styled.div`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const WarmDataDisplay = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const WarmDataRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: rgba(177, 186, 196, 0.12);
  border-radius: 6px;
  border: 1px solid #30363d;
`;

const WarmDataLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #c9d1d9 !important;
  font-size: 0.9rem;
  
  * {
    color: #c9d1d9 !important;
  }
`;

const WarmDataValue = styled.span`
  color: #f0f6fc;
  font-weight: 500;
`;

const FactoryControlsCard = styled(ConfigCard)`
  margin-top: 16px;
`;

const FactoryDataDisplay = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FactoryDataRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: rgba(177, 186, 196, 0.12);
  border-radius: 6px;
  border: 1px solid #30363d;
`;

const FactoryDataLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #c9d1d9;
  font-size: 0.9rem;
`;

const FactoryDataValue = styled.span`
  color: #f0f6fc;
  font-weight: 500;
`;

const ControlButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
`;

const pulse = keyframes`
  0% { opacity: 0.2; transform: scale(0.9); }
  50% { opacity: 1; transform: scale(1); }
  100% { opacity: 0.2; transform: scale(0.9); }
`;

const DialingWrapper = styled.div`
  display: inline-flex;
  gap: 4px;
  margin-left: 6px;
`;

const DialingDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #58a6ff;
  animation: ${pulse} 1.2s infinite ease-in-out;

  &:nth-child(2) {
    animation-delay: 0.2s;
  }

  &:nth-child(3) {
    animation-delay: 0.4s;
  }
`;

const DialingIndicator = () => (
  <DialingWrapper role="status" aria-live="polite" aria-label="Dialing warm nodes">
    <DialingDot />
    <DialingDot />
    <DialingDot />
  </DialingWrapper>
);

const InfoBadge = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 1px solid #30363d;
  background: transparent;
  color: #58a6ff;
  cursor: help;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;

  &:hover,
  &:focus-visible {
    background: rgba(88, 166, 255, 0.12);
    border-color: #58a6ff;
    color: #58a6ff;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(88, 166, 255, 0.25);
  }

  svg {
    font-size: 14px;
  }
`;

const InlineInfoBadge = styled(InfoBadge)`
  width: 20px;
  height: 20px;
  margin-left: 6px;
  border-radius: 6px;
`;

const workerGlow = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(88, 166, 255, 0.3);
  }
  70% {
    box-shadow: 0 0 0 12px rgba(88, 166, 255, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(88, 166, 255, 0);
  }
`;

const WorkerTelemetry = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const WorkerPulse = styled.div<{ $active: boolean }>`
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  font-size: 1.5rem;
  font-weight: 600;
  color: ${(props) => (props.$active ? '#58a6ff' : '#8b949e')};
  padding: 8px 12px;
  border: 1px solid #30363d;
  border-radius: 999px;
  background: rgba(88, 166, 255, 0.08);
  animation: ${(props) => (props.$active ? workerGlow : 'none')} 2.4s infinite;

  span {
    font-size: 1.8rem;
  }

  small {
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }
`;

const CountdownText = styled.div`
  font-size: 0.95rem;
  color: #c9d1d9;
`;

const WorkerProgress = styled.div`
  position: relative;
  height: 6px;
  width: 100%;
  border-radius: 999px;
  background: #21262d;
  overflow: hidden;
`;

const WorkerProgressFill = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: linear-gradient(90deg, #58a6ff, #8b5cf6);
  transition: width 0.3s ease;
`;

// Modal Styles
const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ModalSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ModalSectionTitle = styled.h3`
  margin: 0;
  color: #f0f6fc;
  font-size: 1.1rem;
  border-bottom: 1px solid #30363d;
  padding-bottom: 8px;
`;

const ModalField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ModalFieldLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #c9d1d9;
  font-weight: 500;
`;

const ModalFieldHint = styled.p`
  margin: 4px 0 0;
  color: #8b949e;
  font-size: 0.85rem;
`;

const ModalControlsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
`;

const ModalControlField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ModalControlLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #c9d1d9;
  font-size: 0.9rem;
  font-weight: 500;
`;

// Golden Button Styling - Matching Aso Rock
const GoldenButton = styled(Button)`
  &.ant-btn-primary {
    background: linear-gradient(135deg, #8b6914 0%, #d4af37 100%) !important;
    border-color: #d4af37 !important;
    color: #0a0a0a !important;
    font-weight: 600 !important;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3) !important;
    box-shadow: 0 4px 12px rgba(212, 175, 55, 0.3) !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;

    &:hover {
      background: linear-gradient(135deg, #d4af37 0%, #b8860b 100%) !important;
      border-color: #b8860b !important;
      transform: translateY(-2px) !important;
      box-shadow: 0 6px 20px rgba(212, 175, 55, 0.4) !important;
    }

    &:active {
      transform: translateY(0) scale(0.98) !important;
      box-shadow: 0 2px 8px rgba(212, 175, 55, 0.3) !important;
    }

    &:disabled {
      background: #3a3a3a !important;
      border-color: #3a3a3a !important;
      color: #8b949e !important;
      opacity: 0.6 !important;
    }
  }
`;

// Rate-Limited Alerts Styled Components
const RateLimitedAlertsCard = styled(NodeCard)`
  .ant-card-body {
    padding: 16px !important;
  }
`;

const RateLimitedAlertsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 300px;
  overflow-y: auto;
`;

const RateLimitedAlertItem = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid ${COLORS.border};
  border-radius: 8px;
  padding: 12px;
`;

const RateLimitedAlertHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`;

const RateLimitedAlertMeta = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
`;

